// import { zodResolver } from "@hookform/resolvers/zod";
import { formatDateForInput } from "@/utils/date.utils";
import {
  BLOOD_TYPES,
  Button,
  Calendar, formatPhoneNumberWithCountryCode, Heart,
  Input,
  Mail,
  Modal,
  PhoneInput,
  Select,
  User, Users, X,
} from "@ews-admin/global-design-system";
import { zodResolver } from "@hookform/resolvers/zod";
import React, { useEffect } from "react";
import { type Resolver, Controller, useFieldArray, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import {
  EditPatientFormSchema,
  isBlankPhoneNumber,
  notificationMethodsFor,
  PatientFormSchema,
  useEditPatientFormSchema,
  usePatientFormSchema,
} from "../../../schemas/patient.schema";
import { ResidencyEnum } from "../../../types/doctor.definition";
import { Patient } from "../../../types/patient.definition";
import {
  AcceptedCountriesEnum,
  CivilityEnum,
  CivilityType,
  NotificationPrefEnum,
  RoleEnum,
} from "../../../types/user.definition";
import { LocationForm } from "../LocationForm";

/**
 * MED-1687: the BFF sends null for unset fields (contact email and priority, blood type, address
 * parts...). The schema accepts a missing optional value but not null, so one null anywhere kept
 * "Mettre à Jour" disabled without any message. Nulls become undefined.
 */
const withoutNulls = <T,>(value: T): T => {
  if (Array.isArray(value)) return value.map(withoutNulls) as T;
  if (value && typeof value === "object") {
    return Object.fromEntries(
      Object.entries(value).map(([key, v]) => [key, v === null ? undefined : withoutNulls(v)]),
    ) as T;
  }
  return value;
};

interface PatientFormProps {
  onSubmit: (patientData: PatientFormSchema) => void;
  onCancel: () => void;
  isSubmitting?: boolean;
  initialData?: Partial<Patient>;
  isEditMode?: boolean;
}

export const PatientForm: React.FC<PatientFormProps> = ({
  onSubmit,
  onCancel,
  isSubmitting = false,
  initialData,
  isEditMode = false,
}) => {
  const { t } = useTranslation();
  // Use appropriate schema based on mode
  const createSchema = usePatientFormSchema();
  // The stored number, as the form shows it: unchanged, it does not need the stricter rule.
  const editSchema = useEditPatientFormSchema(
    initialData?.phoneNumber
      ? formatPhoneNumberWithCountryCode(initialData.phoneNumber)
      : undefined,
    (initialData?.emergencyContacts ?? []).map((c) =>
      formatPhoneNumberWithCountryCode(c.phoneNumber || ""),
    ),
  );
  const patientFormSchema = isEditMode ? editSchema : createSchema;

  const defaultValues = React.useMemo(() => {
    if (isEditMode && initialData) {
      // Pre-fill form with patient data for editing
      // Handle locations: use locations array if available, otherwise convert single location to array
      // MED-1687: the address and region are required strings in the schema; null kept the form
      // invalid. "" is shown as an empty required field instead.
      let locations = (initialData.locations || []).map((l) => ({
        ...l,
        address: l.address ?? "",
        state: l.state ?? "",
      }));
      if (locations.length === 0 && initialData.location) {
        // Migrate from old single location format to new array format
        locations = [
          {
            ...initialData.location,
            address: initialData.location.address ?? "",
            state: initialData.location.state ?? "",
            isPrimary: true,
          },
        ];
      }
      // Ensure at least one location exists
      if (locations.length === 0) {
        locations = [
          {
            address: "",
            state: "",
            city: "",
            zip: "",
            country: AcceptedCountriesEnum.SENEGAL,
            residency: ResidencyEnum.OTHER,
            isPrimary: true,
          },
        ];
      }

      return withoutNulls({
        firstName: initialData.firstName || "",
        lastName: initialData.lastName || "",
        email: initialData.email || "",
        phoneNumber: formatPhoneNumberWithCountryCode(initialData.phoneNumber || ""),
        civility: initialData.civility as CivilityType,
        roleName: RoleEnum.PATIENT,
        dateOfBirth: formatDateForInput(initialData.dateOfBirth),
        locations: locations,
        preferredNotificationMethods:
          (initialData.preferredNotificationMethods as (typeof NotificationPrefEnum)[keyof typeof NotificationPrefEnum][]) || [
            NotificationPrefEnum.EMAIL,
          ],
        bloodType: initialData.bloodType ?? "",
        emergencyContacts: (initialData.emergencyContacts || []).map((c) => ({
          ...c,
          phoneNumber: formatPhoneNumberWithCountryCode(c.phoneNumber || ""),
          email: c.email ?? "",
          priority: c.priority ?? 1,
        })),
      });
    }
    // Default values for new patient
    return {
      firstName: "",
      lastName: "",
      email: "",
      phoneNumber: "",
      civility: CivilityEnum.MR,
      roleName: RoleEnum.PATIENT,
      dateOfBirth: "",
      locations: [
        {
          address: "",
          state: "",
          city: "",
          zip: "",
          country: AcceptedCountriesEnum.SENEGAL,
          residency: ResidencyEnum.OTHER,
          isPrimary: true,
        },
      ],
      preferredNotificationMethods: [NotificationPrefEnum.EMAIL],
      emergencyContacts: [],
    };
  }, [initialData, isEditMode]);
  const {
    control,
    handleSubmit,
    formState: { errors, isValid, isDirty },
    watch,
    setValue,
    getValues,
    register,
    trigger,
  } = useForm<PatientFormSchema | EditPatientFormSchema>({
    resolver: zodResolver(patientFormSchema) as Resolver<PatientFormSchema | EditPatientFormSchema>,
    defaultValues,
    mode: "onChange",
    reValidateMode: "onBlur",
  });

  // MED-1687: when editing, show at once what is still invalid in the stored data (a missing
  // address...) instead of a "Mettre à Jour" button disabled with no message.
  useEffect(() => {
    if (isEditMode) trigger();
  }, [isEditMode, trigger]);

  // MED-1687: email or phone number, at least one — the field left empty is only required
  // while the other one is empty too (phone-only patients could not be saved at all).
  const hasEmail = !!watch("email")?.trim();
  const hasPhone = !isBlankPhoneNumber(watch("phoneNumber"));

  // MED-1687: on creation, the notification methods follow the contacts typed: only an email
  // means EMAIL (checked and locked), only a phone means SMS, both let the user choose.
  const methodChoice = notificationMethodsFor(watch("email"), watch("phoneNumber"));
  const forcedMethod = isEditMode ? null : methodChoice.forced;
  const bothContacts = methodChoice.available.length === 2;
  useEffect(() => {
    if (isEditMode) return;
    const current = getValues("preferredNotificationMethods") || [];
    if (forcedMethod) {
      if (current.length !== 1 || current[0] !== forcedMethod) {
        setValue("preferredNotificationMethods", [forcedMethod], { shouldValidate: true });
      }
    } else if (!bothContacts && current.length > 0) {
      setValue("preferredNotificationMethods", [], { shouldValidate: false });
    }
  }, [isEditMode, forcedMethod, bothContacts, getValues, setValue]);
  const withContactBlanksRemoved = <
    T extends { email?: string; phoneNumber?: string; emergencyContacts?: { email?: string }[] },
  >(
    data: T,
  ): T => ({
    ...data,
    email: data.email?.trim() ? data.email : undefined,
    phoneNumber: isBlankPhoneNumber(data.phoneNumber) ? undefined : data.phoneNumber,
    emergencyContacts: data.emergencyContacts?.map((contact) => ({
      ...contact,
      email: contact.email?.trim() ? contact.email : undefined,
    })),
  });

  const {
    fields: emergencyContactFields,
    append: appendEmergencyContact,
    remove: removeEmergencyContact,
  } = useFieldArray({
    control,
    name: "emergencyContacts",
  });


  const addEmergencyContact = () => {
    const newContact = {
      id: `temp-${Date.now()}`, // Generate temporary ID
      firstName: "",
      lastName: "",
      relationship: "",
      phoneNumber: "",
      email: "",
      priority: 1,
    };
    appendEmergencyContact(newContact);
  };

  const handleNotificationMethodsChange = (
    method: string,
    checked: boolean,
  ) => {
    const currentMethods = watch("preferredNotificationMethods") || [];
    if (checked) {
      setValue(
        "preferredNotificationMethods",
        [
          ...currentMethods,
          method as (typeof NotificationPrefEnum)[keyof typeof NotificationPrefEnum],
        ],
        { shouldValidate: true },
      );
    } else {
      // MED-1653: validate right away so unchecking everything shows its error.
      setValue(
        "preferredNotificationMethods",
        currentMethods.filter((m) => m !== method),
        { shouldValidate: true },
      );
    }
  };

  return (
    <Modal
      isOpen
      onClose={onCancel}
      title={t(
        isEditMode ? "patient.form.title.edit" : "patient.form.title.add",
      )}
      variant="info"
      size="4xl"
      contentClassName="max-h-[75vh] overflow-y-auto"
    >
      <p className="mb-6 -mt-2 text-sm text-neutral-500">
        {t(
          isEditMode
            ? "patient.form.subtitle.edit"
            : "patient.form.subtitle.add",
        )}
      </p>
      {!isEditMode && (
        <p className="mb-6 -mt-4 text-sm text-neutral-500">
          {t("patient.form.subtitle.temporaryPassword")}
        </p>
      )}
      <form
              onSubmit={handleSubmit((data) => {
                // Never send password fields: only the edit schema has them (optional), and a new
                // patient gets a temporary password from the API (MED-1687)
                const {
                  password: _password,
                  confirmPassword: _confirmPassword,
                  ...rest
                } = data as typeof data & { password?: string; confirmPassword?: string };
                onSubmit(withContactBlanksRemoved(rest) as unknown as PatientFormSchema);
              })}
              className="space-y-6"
            >
              {/* Personal Information */}
              <div className="space-y-4">
                <h3 className="flex items-center text-lg font-medium text-neutral-900">
                  <User className="mr-2 w-5 h-5 text-primary-600" />
                  {t("patient.form.sections.personal")}
                </h3>

                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <div>
                    <Select
                      size="md"
                      label={t("patient.form.fields.civility")}
                      options={[
                        {
                          value: "",
                          label: t("patient.form.fields.selectCivility"),
                        },
                        {
                          value: CivilityEnum.MR,
                          label: t("patient.form.fields.mr"),
                        },
                        {
                          value: CivilityEnum.MRS,
                          label: t("patient.form.fields.mrs"),
                        },
                      ]}
                      value={watch("civility")}
                      onChange={(value) =>
                        setValue("civility", value as CivilityType)
                      }
                      disabled={isSubmitting}
                      error={errors.civility?.message}
                      required
                    />
                  </div>
                  <div>
                    <Input
                      required
                      size="sm"
                      label={t("patient.form.fields.dateOfBirth")}
                      type="date"
                      {...register("dateOfBirth")}
                      error={errors.dateOfBirth?.message}
                      disabled={isSubmitting}
                      leftIcon={<Calendar size={16} />}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <Input
                    required
                    label={t("patient.form.fields.firstName")}
                    {...register("firstName")}
                    placeholder={t("patient.form.placeholders.firstName")}
                    error={errors.firstName?.message}
                    disabled={isSubmitting}
                  />

                  <Input
                    required
                    label={t("patient.form.fields.lastName")}
                    {...register("lastName")}
                    placeholder={t("patient.form.placeholders.lastName")}
                    error={errors.lastName?.message}
                    disabled={isSubmitting}
                  />
                </div>

                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <Input
                    required={!hasPhone}
                    label={t("patient.form.fields.email")}
                    type="email"
                    {...register("email", { deps: ["phoneNumber"] })}
                    placeholder={t("patient.form.placeholders.email")}
                    error={errors.email?.message}
                    disabled={isEditMode || isSubmitting}
                    leftIcon={<Mail size={16} />}
                  />

                  <Controller
                    control={control}
                    name="phoneNumber"
                    rules={{ deps: ["email"] }}
                    render={({ field, fieldState }) => (
                      <PhoneInput
                        required={!hasEmail}
                        label={t("patient.form.fields.phoneNumber")}
                        value={field.value}
                        onChange={field.onChange}
                        onBlur={field.onBlur}
                        name={field.name}
                        ref={field.ref}
                        placeholder={t("patient.form.placeholders.phoneNumber")}
                        error={fieldState.error?.message}
                        disabled={isSubmitting}
                      />
                    )}
                  />
                </div>

                <div>
                  <label className="block mb-1 text-sm font-medium text-neutral-700">
                    {t("patient.form.fields.preferredNotificationMethods")}{" "}
                    <span className="text-red-500">*</span>
                  </label>
                  <div className="flex space-x-4">
                    {Object.values(NotificationPrefEnum).map((method) => (
                      <label key={method} className="flex items-center">
                        <Input
                          type="checkbox"
                          checked={
                            watch("preferredNotificationMethods")?.includes(
                              method,
                            ) || false
                          }
                          onChange={(e) =>
                            handleNotificationMethodsChange(
                              method,
                              e.target.checked,
                            )
                          }
                          className="mr-2 rounded border-neutral-300 text-primary-600 focus:ring-primary-500"
                          disabled={
                            isSubmitting ||
                            (!isEditMode && (!!forcedMethod || !methodChoice.available.includes(method)))
                          }
                        />
                        <span className="text-sm text-neutral-700">
                          {method}
                        </span>
                      </label>
                    ))}
                  </div>
                  {errors.preferredNotificationMethods && (
                    <p className="mt-1 text-sm text-red-600">
                      {errors.preferredNotificationMethods.message}
                    </p>
                  )}
                </div>
              </div>

              {/* Address */}
              <LocationForm
                control={control}
                setValue={setValue}
                errors={errors}
                addressRequired
              />

              {/* Medical Information */}
              {!isEditMode && (
                <div className="space-y-4">
                  <h3 className="flex items-center text-lg font-medium text-neutral-900">
                    <Heart className="mr-2 w-5 h-5 text-primary-600" />
                    {t("patient.form.sections.medical")}
                  </h3>

                  <div>
                    <Select
                      label={t("patient.form.fields.bloodType")}
                      placeholder={t("patient.form.fields.selectBloodType")}
                      options={[
                        {
                          value: null,
                          label: t("patient.form.fields.selectBloodType"),
                        },
                        ...BLOOD_TYPES.filter((bt) => bt !== "UNKNOWN").map(
                          (bt) => ({ value: bt, label: bt }),
                        ),
                      ]}
                      value={watch("bloodType")}
                      onChange={(value) =>
                        setValue(
                          "bloodType",
                          value == null ? undefined : (value as string),
                        )
                      }
                      disabled={isSubmitting}
                      error={errors.bloodType?.message}
                    />
                  </div>
                </div>
              )}

              {/* Emergency Contacts */}
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <h3 className="flex items-center text-lg font-medium text-neutral-900">
                    <Users className="mr-2 w-5 h-5 text-primary-600" />
                    {t("patient.form.sections.emergency")}
                  </h3>
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={addEmergencyContact}
                    disabled={isSubmitting}
                    className="text-sm"
                  >
                    {t("patient.form.emergencyContact.addContact")}
                  </Button>
                </div>

                {emergencyContactFields.map((field, index) => (
                  <div
                    key={field.id}
                    className="p-4 space-y-4 rounded-lg border border-neutral-200"
                  >
                    <div className="flex justify-between items-center">
                      <h4 className="font-medium text-neutral-900">
                        {t("patient.form.emergencyContact.title", {
                          number: index + 1,
                        })}
                      </h4>
                      <button
                        type="button"
                        onClick={() => removeEmergencyContact(index)}
                        className="p-1 text-red-500 hover:text-red-700"
                        disabled={isSubmitting}
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>

                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                      <Input
                        required
                        label={t("patient.form.emergencyContact.name")}
                        {...register(`emergencyContacts.${index}.firstName`)}
                        placeholder={t("patient.form.placeholders.contactName")}
                        disabled={isSubmitting}
                        error={
                          errors.emergencyContacts?.[index]?.firstName?.message
                        }
                      />
                      <Input
                        required
                        label={t("patient.form.emergencyContact.name")}
                        {...register(`emergencyContacts.${index}.lastName`)}
                        placeholder={t("patient.form.placeholders.contactName")}
                        disabled={isSubmitting}
                        error={
                          errors.emergencyContacts?.[index]?.lastName?.message
                        }
                      />

                      <Select
                        label={t("patient.form.emergencyContact.relationship")}
                        value={watch(`emergencyContacts.${index}.relationship`)}
                        onChange={(value) =>
                          setValue(
                            `emergencyContacts.${index}.relationship`,
                            value as string,
                          )
                        }
                        placeholder={t(
                          "patient.form.placeholders.relationship",
                        )}
                        options={[
                          {
                            value: "SPOUSE",
                            label: t(
                              "patient.form.emergencyContact.relationships.SPOUSE",
                            ),
                          },
                          {
                            value: "FAMILY",
                            label: t(
                              "patient.form.emergencyContact.relationships.FAMILY",
                            ),
                          },
                          {
                            value: "FRIEND",
                            label: t(
                              "patient.form.emergencyContact.relationships.FRIEND",
                            ),
                          },
                          {
                            value: "OTHER",
                            label: t(
                              "patient.form.emergencyContact.relationships.OTHER",
                            ),
                          },
                        ]}
                        disabled={isSubmitting}
                        error={
                          errors.emergencyContacts?.[index]?.relationship
                            ?.message
                        }
                        required
                      />

                      <Controller
                        control={control}
                        name={`emergencyContacts.${index}.phoneNumber`}
                        render={({ field, fieldState }) => (
                          <PhoneInput
                            required
                            label={t("patient.form.emergencyContact.phoneNumber")}
                            value={field.value}
                            onChange={field.onChange}
                            onBlur={field.onBlur}
                            name={field.name}
                            ref={field.ref}
                            placeholder={t("patient.form.placeholders.contactPhone")}
                            disabled={isSubmitting}
                            error={fieldState.error?.message}
                          />
                        )}
                      />

                      <Input
                        label={t("patient.form.emergencyContact.email")}
                        type="email"
                        {...register(`emergencyContacts.${index}.email`)}
                        placeholder={t(
                          "patient.form.placeholders.contactEmail",
                        )}
                        disabled={isSubmitting}
                        leftIcon={<Mail size={16} />}
                        error={
                          errors.emergencyContacts?.[index]?.email?.message
                        }
                      />
                    </div>

                    <Select
                      label={t("patient.form.emergencyContact.priority")}
                      value={String(watch(`emergencyContacts.${index}.priority`) ?? 1)}
                      onChange={(value) =>
                        setValue(
                          `emergencyContacts.${index}.priority`,
                          Number(value),
                        )
                      }
                      options={[
                        { value: "0", label: t("patient.form.emergencyContact.priorities.0") },
                        { value: "1", label: t("patient.form.emergencyContact.priorities.1") },
                        { value: "2", label: t("patient.form.emergencyContact.priorities.2") },
                        { value: "3", label: t("patient.form.emergencyContact.priorities.3") },
                        { value: "4", label: t("patient.form.emergencyContact.priorities.4") },
                      ]}
                      disabled={isSubmitting}
                    />
                  </div>
                ))}

                {emergencyContactFields.length === 0 && (
                  <div className="py-8 text-center text-neutral-500">
                    <Users className="mx-auto mb-2 w-12 h-12 text-neutral-300" />
                    <p>{t("patient.form.emergencyContact.noContacts")}</p>
                    <p className="text-sm">
                      {t("patient.form.emergencyContact.clickToAdd")}
                    </p>
                  </div>
                )}
              </div>

              {/* Form Actions */}
              <div className="flex justify-end items-center pt-6 space-x-3 border-t border-neutral-200">
                <Button
                  type="button"
                  variant="ghost"
                  onClick={onCancel}
                  disabled={isSubmitting}
                >
                  {t("patient.form.actions.cancel")}
                </Button>
                <Button
                  type="submit"
                  variant="ews-primary"
                  disabled={isSubmitting || !isValid || !isDirty}
                  loading={isSubmitting}
                >
                  {isSubmitting
                    ? isEditMode
                      ? t("patient.form.actions.updating")
                      : t("patient.form.actions.creating")
                    : isEditMode
                      ? t("patient.form.actions.update")
                      : t("patient.form.actions.create")}
                </Button>
              </div>
            </form>
    </Modal>
  );
};
